import { useEffect, useMemo, useRef, useState } from 'react';
import './device-details.styles.scss';

import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';

import CircularProgressLoader from '@lifepowr/components/src/components-shared/circular-progress-loader-component/circular-progress-loader.component';

import { API } from 'Amplify';
import { cloneDeep, debounce, get, isEmpty, isEqual, merge, orderBy } from 'lodash';

import _formSchema from './formSchema.json';
import legacyFormSchema from './legacyFormSchema.json';
import _dsoTariffOptions from './dso-tariff-options.json';

import { useDeviceDataApiHook, useDeviceSchemaApiHook } from '../shared/device-data-api.hook';
import {
  generateBatteryDefinitionSchemaMap,
  generateConverterSchema,
  generateNewFormConfig,
  getBatteryDefinitionList,
  getConverterDetailsByBrandModel,
  isInvertorHybrid,
  isTariffCountryNameValid,
  setBatteryDefinitionList,
} from '../shared/device-details-utils';

import { type ApiClientStatus, ApiClientStatusEnum } from '@lifepowr/components/src/utils/api-client';

import validator from '@rjsf/validator-ajv8';
import type { FormProps } from '@rjsf/core';
import Form from '@rjsf/mui';
import FieldTemplate from '@rjsf/mui/lib/FieldTemplate';
import type { CustomValidator, FormValidation, RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import { DEFAULT_SCHEMA_KEYS, filterOutSchemaFields, UiSchemaGenerator, USER_NONE_ADVANCED_SCHEMA_KEYS } from '../shared/ui-schema-generator';
import type { AdditionalInformation, DeviceAddressObject, DeviceConfiguration, Dictionary } from '../shared/types';
import type { JSONSchema4 } from 'json-schema';
import { SchemaLoadingFailureAlertBox } from '../shared/schema-failure-alert.component';
import { DeviceDynamicDataSubscriber } from '../shared/device-dynamic-data-subscriber';
import { type IntegrationListResponse, useIntegrationsApiHook } from '@lifepowr/components/src/components/Fleet/cloudflex-api.hook';
import CircularProgress from '@mui/material/CircularProgress';

const FIELD_TYPES = {
  FIELD_NUMBER: 'field-number',
  FIELD_STRING: 'field-string',
  FIELD_BOOLEAN: 'field-boolean',
  FIELD_OBJECT: 'field-object',
  FIELD_ARRAY: 'field-array',
};

const _batteryFieldMatcherRegex = new RegExp(/^root_settings_converters_[\d]+_battery$/);
const _tariffMatcherRegex = new RegExp(/^root_settings_customInjectionTariffStructure_0$/);
const hiddenErrorNames = new Set<string>(['anyOf', 'allOf', 'oneOf']);

// Define structure for dso-tariff-options.json
type TariffEntry = { mtuIdx: number; price: number };
type DsoTariff = {
  METADATA: { internalName: string };
  MONDAY: TariffEntry[];
  TUESDAY: TariffEntry[];
  WEDNESDAY: TariffEntry[];
  THURSDAY: TariffEntry[];
  FRIDAY: TariffEntry[];
  SATURDAY: TariffEntry[];
  SUNDAY: TariffEntry[];
};
type DsoTariffOption = {
  id: string;
  displayName: string;
  description: string;
  tariff: DsoTariff;
};
type DsoTariffOptionsMap = Record<string, Record<string, DsoTariffOption>>;

const dsoTariffOptions: DsoTariffOptionsMap = _dsoTariffOptions;

function populateFieldsWithDefaults(props: any, mappingRegex: RegExp) {
  if (props.id.match(mappingRegex) !== null && props.schema.properties !== undefined) {
    Object.entries(props.schema.properties).forEach(([fieldName, fieldSchema]: [string, any]) => {
      if (!props.uiSchema) {
        return;
      }

      if (fieldSchema.const !== undefined) {
        if (!(fieldName in props.uiSchema)) {
          props.uiSchema[fieldName] = {};
        }

        props.uiSchema[fieldName]['ui:disabled'] = true;
        props.formData[fieldName] = fieldSchema.const;
      } else if (fieldName in props.uiSchema) {
        delete props.uiSchema[fieldName]['ui:disabled'];
      }
    });
  }
}

/**
 * Creates a custom field template component that highlights differences between desired and reported values
 *
 * The component will:
 * - Check if the field is a primitive type (string, number, boolean)
 * - Look up if there are differences for this field path in the diffObj
 * - Add a 'diff-class' to highlight fields with differences
 * - Render using the base Templates.FieldTemplate
 */
const CustomFieldTemplate = ({ classNames, children, ...props }: any) => {
  const { diffObj } = props.formContext;
  const isPrimitive =
    classNames.includes(FIELD_TYPES.FIELD_STRING) || classNames.includes(FIELD_TYPES.FIELD_NUMBER) || classNames.includes(FIELD_TYPES.FIELD_BOOLEAN);
  const diff = get(diffObj, props.id.replace('root_', '').replaceAll('_', '.'));
  const rootDiff = diff?.root;

  populateFieldsWithDefaults(props, _batteryFieldMatcherRegex);
  populateFieldsWithDefaults(props, _tariffMatcherRegex);

  const updatedClassNames = (diff && isPrimitive) || rootDiff ? `diff-class ${classNames}` : classNames;

  return (
    <FieldTemplate
      classNames={updatedClassNames}
      {...props}
    >
      {children}
    </FieldTemplate>
  );
};

function isPrimitive(value: any) {
  return typeof value !== 'object' && value !== undefined;
}

function calculateDiffObject(desired: any, reported: any) {
  if (!desired || !reported) {
    return {};
  }

  return Object.keys(desired).reduce((acc: any, key: string) => {
    const desiredValue = desired[key];
    const reportedValue = reported[key];

    // Skip if values are equal
    if (isEqual(desiredValue, reportedValue)) {
      return acc;
    }

    // Handle primitive values
    if (isPrimitive(desiredValue)) {
      acc[key] = true;
      //acc[key] = {"ui:classNames": "diff-class"};
    }

    // Handle nested objects recursively
    if (typeof desiredValue === 'object') {
      acc[key] = calculateDiffObject(desiredValue, reportedValue);
    }

    // Handle arrays recursively
    if (Array.isArray(desiredValue)) {
      acc[key] = desiredValue.map((_, index) => calculateDiffObject(desiredValue[index], reportedValue[index]));
    }

    return acc;
  }, {});
}

// By default, we show limited set of configurations for end users. Otherwise, show the default set.
function removeSchemaFieldsAccordingToUserType(schema: any, renderUiInUserMode: boolean, hasUserEnabledAdvancedUi: boolean) {
  const keysToKeep = renderUiInUserMode && !hasUserEnabledAdvancedUi ? USER_NONE_ADVANCED_SCHEMA_KEYS : DEFAULT_SCHEMA_KEYS;
  const filteredProperties = filterOutSchemaFields(schema, keysToKeep);
  const requiredFields: string[] = [];

  keysToKeep.forEach((key) => {
    if (schema.required.includes(key)) {
      requiredFields.push(key);
    }
  });

  return {
    type: 'object',
    properties: filteredProperties,
    title: '',
    required: requiredFields,
  };
}

function removeNonImportantSchemaFieldAttributes(schema: JSONSchema4): JSONSchema4 {
  return Object.fromEntries(
    Object.entries(schema).filter(([k, _]) => ['properties', 'minProperties', 'title', 'description', 'required', 'definitions'].includes(k)),
  );
}

function selectDeviceConfigurationSchemaBasedOnConfigurationData(configuration: any, isCfuDevice: boolean) {
  const shouldUseAddressObject = configuration === undefined || 'addressObj' in configuration || !('address' in configuration);
  const configurationSchema: any = shouldUseAddressObject ? _formSchema : legacyFormSchema;

  configurationSchema.properties.converter = generateConverterSchema(configuration, isCfuDevice);

  return configurationSchema;
}

const priorityBatteries = ['NoBattery', 'UnsupportedBattery', 'LifepowrBattery'];

function sortBatteryDefinitionsAlphabeticallyByTitle(schemaDefinitions: JSONSchema4, batteryDefinitions: JSONSchema4[]) {
  return orderBy(batteryDefinitions, (ref: any) => {
    const [__, batteryKey] = ref.$ref.split('#/definitions/');

    const priorityIndex = priorityBatteries.indexOf(batteryKey);
    if (priorityIndex !== -1) {
      return -1000 + priorityIndex; // Negative values to sort to the top
    }

    const batteryConfig = get(schemaDefinitions, batteryKey);
    if (batteryConfig) {
      return batteryConfig.title;
    }

    return '';
  });
}

/**
 * Sorts the battery titles in the schema definitions alphabetically.
 */
function sortBatteryTitles(schemaDefinitions: JSONSchema4): any {
  const batterySchemaDefinition = schemaDefinitions.Converter?.properties?.battery;

  if (batterySchemaDefinition) {
    const batteryDefinitionList = getBatteryDefinitionList(batterySchemaDefinition);
    setBatteryDefinitionList(batterySchemaDefinition, sortBatteryDefinitionsAlphabeticallyByTitle(schemaDefinitions, batteryDefinitionList));
  }
}

type SanitizedDeviceSchema = {
  definitions: JSONSchema4['definitions'];
  properties: {
    configuration: any;
    settings: any;
  };
};

function sanitizeDeviceRawSchema(
  deviceRawSchema: JSONSchema4,
  renderUiInUserMode: boolean,
  hasUserEnabledAdvancedUi: boolean,
  configuration: any,
  isCfuDevice: boolean,
): SanitizedDeviceSchema {
  const schemaWithOnlyTheImportantFieldAttributes = removeNonImportantSchemaFieldAttributes(deviceRawSchema);

  const deviceSchema = removeSchemaFieldsAccordingToUserType(deviceRawSchema, renderUiInUserMode, hasUserEnabledAdvancedUi);

  const configurationSchema =
    !renderUiInUserMode || hasUserEnabledAdvancedUi ? selectDeviceConfigurationSchemaBasedOnConfigurationData(configuration, isCfuDevice) : {};

  return {
    definitions: schemaWithOnlyTheImportantFieldAttributes.definitions,
    properties: {
      configuration: configurationSchema,
      settings: deviceSchema,
    },
  };
}

/**
 * Augments a sanitized device schema with additional fields that should be visible in the form
 * but are not part of the original device schema.
 */
function augmentDeviceSchema(sanitizedSchema: SanitizedDeviceSchema | {}): SanitizedDeviceSchema | {} {
  if (!sanitizedSchema || isEmpty(sanitizedSchema)) {
    return {};
  }

  // Deep copy to avoid mutating the original sanitizedDeviceSchema
  const augmentedSchema = cloneDeep(sanitizedSchema) as SanitizedDeviceSchema;

  // Add batteryDescription field to UnsupportedBattery for Device Configuration
  // This field is used to collect information about unsupported batteries
  if (augmentedSchema?.definitions?.UnsupportedBattery?.properties) {
    augmentedSchema.definitions.UnsupportedBattery.properties.batteryDescription = {
      title: 'Battery Description',
      description: 'Description (Brand, model, high/low voltage)',
      type: 'string',
      maxLength: 250,
    };
  }

  // Add dsoTariffDropdown field to Wallonia region in Belgium
  // Navigate through the countryStructure to find Belgium -> Wallonia -> segmentStructure
  let countryStructure = augmentedSchema?.properties?.settings?.properties?.countryStructure;
  if (countryStructure?.$ref) {
    // The countryStructure definition moved from properties to definitions in the schema, so we need to handle both cases.
    countryStructure = augmentedSchema?.definitions?.countryStructure;
  }
  if (countryStructure?.oneOf) {
    const belgiumStructure = countryStructure.oneOf.find((country: any) => country.title === 'Belgium');
    if (belgiumStructure?.properties?.regionStructure?.oneOf) {
      const walloniaStructure = belgiumStructure.properties.regionStructure.oneOf.find((region: any) => region.title === 'Wallonia');
      const walloniaSegmentStructure = walloniaStructure?.properties?.segmentStructure.oneOf[0];
      if (walloniaSegmentStructure?.properties) {
        // Add base dsoTariffDropdown field
        walloniaSegmentStructure.properties.dsoTariffDropdown = {
          title: 'DSO Tariff Type',
          description: 'Select the DSO tariff structure type',
          type: 'string',
        };

        if (augmentedSchema?.definitions?.Metadata) {
          if (!augmentedSchema.definitions.Metadata.properties) {
            augmentedSchema.definitions.Metadata.properties = {};
          }

          augmentedSchema.definitions.Metadata.properties.internalName = {
            title: 'DSO Tariff Name',
            type: 'string',
          };
        }

        // Add dropdown options based on the DSO field
        const dsoOneOf = Object.entries(dsoTariffOptions || {})
          .map(([dsoKey, dsoOptions]) => {
            // Skip if the value for this DSO key is null/undefined
            if (!dsoOptions) {
              console.log('DSO tariff options missing for DSO key:', dsoKey);
              return null;
            }

            const tariffOptions = Object.values(dsoOptions);

            // Filter out any malformed entries that lack an ID
            const validOptions = tariffOptions.filter((opt) => opt?.id);

            if (validOptions.length === 0) {
              console.log('No valid tariff options found for DSO key:', dsoKey);
              return {
                properties: {
                  dso: { allOf: [{ const: dsoKey }] },
                  dsoTariffDropdown: {
                    enum: [],
                    enumNames: [],
                  },
                },
              };
            }

            return {
              properties: {
                dso: { const: dsoKey },
                dsoTariffDropdown: {
                  enum: validOptions.map((opt) => opt.id),
                  enumNames: validOptions.map((opt) => opt.displayName || opt.id),
                  default: validOptions[0]?.id,
                },
              },
            };
          })
          // Filter out any null entries from the map above (e.g., due to malformed DSO options)
          .filter((item) => item !== null);

        walloniaSegmentStructure.dependencies = {
          ...(walloniaSegmentStructure.dependencies || {}),
          dso: {
            oneOf: dsoOneOf,
          },
        };
      }
    }
  }

  return augmentedSchema;
}

/**
 * Traverses the schema and collects the max length of all the Tariff Structures it encounters
 * @param schema
 */
export function findMaxTariffStructureLength(schema: JSONSchema4): number {
  let maxTariffLength = 0;

  if (Array.isArray(schema)) {
    schema.forEach((item) => {
      const tariffStructureLength = findMaxTariffStructureLength(item);
      maxTariffLength = Math.max(maxTariffLength, tariffStructureLength);
    });
  } else if (schema && typeof schema === 'object') {
    let tariffStructureLength = 0;

    if (schema.properties) {
      tariffStructureLength = findMaxTariffStructureLength(schema.properties);
    } else if (schema.oneOf) {
      tariffStructureLength = findMaxTariffStructureLength(schema.oneOf);
    } else if (schema.tariffStructure) {
      tariffStructureLength = schema.tariffStructure.oneOf?.length ?? 0;
    } else {
      for (const [_key, value] of Object.entries(schema)) {
        if (value.type !== 'object') {
          continue;
        }

        tariffStructureLength = findMaxTariffStructureLength(value);
        maxTariffLength = Math.max(maxTariffLength, tariffStructureLength);
      }
    }

    maxTariffLength = Math.max(maxTariffLength, tariffStructureLength);
  }

  return maxTariffLength;
}

function generateUiSchemaBasedOnConfiguration(_settings: any, configuration: any, schema?: JSONSchema4, isCfuDevice: boolean = false) {
  const { converter: chosenConverter = 'DefaultInverter' } = configuration || {};
  let brand: string;
  let model: string;

  if (typeof chosenConverter === 'string') {
    let rest: any;
    [brand, ...rest] = chosenConverter.split(' ');
    model = rest.join(' ');
  } else {
    ({ brand, model } = chosenConverter);
  }

  // The countryStructure definition moved from properties to definitions in the schema, so we need to handle both cases.
  let countryStructure = schema?.properties?.settings?.properties?.countryStructure;
  if (countryStructure?.$ref) {
    countryStructure = schema?.definitions?.countryStructure;
  }
  const maxTariffLength = countryStructure ? findMaxTariffStructureLength(countryStructure) : 0;

  const showHybridPvPanels = isInvertorHybrid({ brand, model }, isCfuDevice) || model === 'Custom';

  // The numberOfInjectionTariffs value affects schemas prior to schema rework (v0.34.1 ???)
  const numberOfInjectionTariffs = schema?.definitions?.AnyTariffStructure?.anyOf?.length || 0;

  const uiSchema = new UiSchemaGenerator.DeviceSettingsBuilder()
    .setConverterPvPanelsVisible(showHybridPvPanels)
    .setSettingsOrdered(true)
    .setConverterWidgetVisible(brand !== 'DefaultInverter')
    .setConverterSettingsVisible(model === 'Custom')
    .setNumberOfCustomInjectionTariffFields(numberOfInjectionTariffs)
    .setMaxTariffLength(maxTariffLength)
    .build();

  // The converter model labels are the enumNames of each brand branch in the schema.
  // Do not set ui:enumNames here: it would belong to no branch.
  return uiSchema;
}

function mergeTopLevelKeys(oldObj: any, newObj: any): any {
  const result = { ...oldObj };
  for (const key of Object.keys(newObj)) {
    result[key] = newObj[key];
  }
  return result;
}

function postProcessInverterSettings(mergedSettings: any, brand: string, model: string, differentInverterHasBeenChosen: boolean, isCfuDevice: boolean) {
  // Removing the PV Arrays from the settings if the converter is not a hybrid inverter (or custom).
  if (!(isInvertorHybrid({ brand, model }, isCfuDevice) || model === 'Custom')) {
    if (mergedSettings.converters[0]?.pvArrays) {
      mergedSettings.converters[0].pvArrays = [];
    }
  }

  // If the inverter has changed, we need to set the default settings for the new inverter, so we override the old ones.
  if (differentInverterHasBeenChosen && model !== 'Custom') {
    const defaultInverterSettings = getConverterDetailsByBrandModel({ brand, model }, isCfuDevice);
    mergedSettings.converters[0] = merge(mergedSettings.converters[0], defaultInverterSettings);
  }
}

function postProcessDSOTariffSettings(settings: any) {
  const segmentStructure = settings?.countryStructure?.regionStructure?.segmentStructure;
  const isWallonia = settings?.countryStructure?.countryName === 'Belgium' && settings?.countryStructure?.regionStructure?.regionName === 'Wallonia';
  if (!segmentStructure) {
    return;
  } else if (!isWallonia) {
    // If it's not Wallonia region, we set `dsoTariff` to null, so it removes the value from
    // the device's shadow.
    segmentStructure.dsoTariff = null;
    return;
  }

  const dso: string = segmentStructure.dso;
  const dsoTariffType: string | undefined = segmentStructure.dsoTariffDropdown;

  // Look up the tariff options for the selected DSO, then find the selected tariff type
  const dsoOptions = dsoTariffOptions[dso];
  if (dsoTariffType && dsoOptions) {
    const preset = dsoOptions[dsoTariffType];
    if (preset) {
      segmentStructure.dsoTariff = preset.tariff;
    }
  }

  // Remove the dropdown field as it's not part of the actual device settings
  delete segmentStructure.dsoTariffDropdown;
}

function generateNewConfigurationDto(
  settings: any,
  desiredConfiguration: DeviceConfiguration,
  desiredSettings: any,
  formSettings: any,
  clientToken: string,
  addressObj: DeviceAddressObject,
  address: string,
  brand: string,
  model: string,
  reference: string,
  desiredAdditionalInformation: AdditionalInformation,
  isCfuDevice: boolean,
): any {
  const cloneSettings = cloneDeep(settings);

  // Remove the current country structure from the settings, as it'll be replaced with the new one.
  // Merging the old with the new values will lead to inconsistencies.
  delete cloneSettings.countryStructure;
  const mergedSettings = mergeTopLevelKeys(cloneSettings, formSettings);

  const additionalInformation: AdditionalInformation = {};

  const body: any = {
    formData: {},
    clientToken,
  };

  // Extract the battery description field and remove it from the settings, because we don't want to
  // store it in the settings.
  const rawUnsupportedBatteryDescription = mergedSettings.converters[0]?.battery?.batteryDescription;
  delete mergedSettings.converters[0]?.battery?.batteryDescription;

  const unsupportedBatteryDescription = determineUnsupportedBatteryDescription(rawUnsupportedBatteryDescription, desiredAdditionalInformation);
  if (unsupportedBatteryDescription !== undefined) {
    additionalInformation.unsupportedBatteryDescription = unsupportedBatteryDescription;
  }

  if (isEqual(additionalInformation, desiredAdditionalInformation)) {
    body.formData.additionalInformation = {};
  } else {
    body.formData.additionalInformation = additionalInformation;
  }

  const newConfiguration = {
    address: !isEmpty(addressObj) ? addressObj : address,
    converter: `${brand} ${model}`,
    reference,
  };

  let differentInverterHasBeenChosen = false;

  if (desiredConfiguration) {
    const existingConfiguration = {
      address: desiredConfiguration.addressObj || desiredConfiguration.address,
      converter: desiredConfiguration.converter,
      reference: desiredConfiguration.reference,
    };

    if (isEqual(newConfiguration, existingConfiguration)) {
      body.formData.configuration = {};
    } else {
      body.formData.configuration = newConfiguration;

      differentInverterHasBeenChosen = desiredConfiguration.converter !== newConfiguration.converter;
    }
  } else {
    body.formData.configuration = newConfiguration;
    differentInverterHasBeenChosen = true;
  }

  // Warning: The following functions modify the mergedSettings object directly.
  postProcessInverterSettings(mergedSettings, brand, model, differentInverterHasBeenChosen, isCfuDevice);
  postProcessDSOTariffSettings(mergedSettings);

  if (isEqual(mergedSettings, desiredSettings)) {
    body.formData.settings = {};
  } else {
    body.formData.settings = mergedSettings;
  }

  return body;
}

function determineUnsupportedBatteryDescription(
  rawUnsupportedBatteryDescription: string | undefined,
  desiredAdditionalInformation: AdditionalInformation,
): string | null | undefined {
  let unsupportedBatteryDescription: string | null | undefined;
  if (rawUnsupportedBatteryDescription) {
    // The UnsupportedBattery was chosen, so we have the battery description field.
    unsupportedBatteryDescription = rawUnsupportedBatteryDescription;
  } else if (desiredAdditionalInformation.unsupportedBatteryDescription) {
    // The UnsupportedBattery was not chosen, so we don't have the battery description field, but
    // the field was set before and we need to remove it. So, we set the field to `null`.
    unsupportedBatteryDescription = null;
  } else {
    // The UnsupportedBattery was not chosen, so we don't have the battery description field.
    // Additionally, the field was not set before, so we can basically ignore it.
    unsupportedBatteryDescription = undefined;
  }
  return unsupportedBatteryDescription;
}

function filterOutIrrelevantBatteryErrors(errors: RJSFValidationError[], form: any): RJSFValidationError[] {
  const batterySchemaMap = (form.props.formContext as FormContext).batterySchemaMap;
  const selectedBattery = form.state.formData?.settings?.converters?.[0]?.battery.batteryType;
  const selectedBatteryDefinition = '#/definitions/' + batterySchemaMap[selectedBattery];

  return errors.filter((error) => {
    if (error.property?.startsWith('.settings.converters.0.battery.')) {
      return error.schemaPath?.startsWith(selectedBatteryDefinition);
    }

    return true;
  });
}

function transformErrors(errors: RJSFValidationError[], _: any, form: any): RJSFValidationError[] {
  if (!form) {
    return errors;
  }

  const filterOutHiddenErrorNames = (error: RJSFValidationError) => !error.name || !hiddenErrorNames.has(error.name);

  const filteredErrors = filterOutIrrelevantBatteryErrors(errors, form);

  return filteredErrors.filter(filterOutHiddenErrorNames).map((error) => {
    if (error.name === 'required') {
      error.message = 'You must provide a value for this field';
    } else if (error.name === 'maximum') {
      error.message = 'This value is too high, it must be less than or equal to ' + error.params.limit;
    } else if (error.name === 'minimum') {
      error.message = 'This value is too low, it must be greater than or equal to ' + error.message;
    }

    return error;
  });
}

const customValidate: CustomValidator = (formData, errors, _uiSchema?): FormValidation => {
  if (formData.configuration === undefined) {
    return errors;
  }

  if (!isTariffCountryNameValid(formData.configuration.addressObj?.country, formData.settings.countryStructure?.countryName)) {
    (errors as any).settings.countryStructure.addError('The tariff country must match the configured country name or be set to CUSTOM');
  }

  return errors;
};

const CONFIG_SAVE_SERVER_ERROR =
  'The configuration could not be saved because of a server error. Your changes have been kept, please verify the values you changed and try ' +
  'submitting again. If the problem persists, please contact support.';

/**
 * Derives a message to show the user for a failed configuration save.
 *
 * A 4xx body describes something the user can act on, so it is shown verbatim. A 5xx body is not
 * actionable (the API answers with an opaque "An unexpected error occurred"), so we show the generic
 * message instead. A missing status (network/CORS failure) falls through to the generic message too.
 */
function getSubmitErrorMessage(e: any): string {
  const status = e?.response?.status;
  const data = e?.response?.data;
  const serverMessage = [data?.error, data?.message, data?.body].find((m) => typeof m === 'string' && m.trim().length > 0);

  if (status >= 400 && status < 500 && serverMessage) {
    return serverMessage;
  }

  return CONFIG_SAVE_SERVER_ERROR;
}

const empty = {};

type FormContext = {
  diffObj: {
    settings: any;
    configuration: any;
  };
  batterySchemaMap: Dictionary<string>;
};

type ConfigurationPartProps = {
  hasUserEnabledAdvancedUi: boolean;
  renderUiInUserMode: boolean;
  thingName: string;
  desiredSettings: any;
  reportedSettings: any;
  desiredConfiguration: any;
  reportedConfiguration: any;
  settingsInTransit: boolean;
  configurationInTransit: boolean;
  cfu?: Record<string, any>;
  additionalInformation: AdditionalInformation;
};

export function ConfigurationPart(props: ConfigurationPartProps) {
  const {
    hasUserEnabledAdvancedUi,
    renderUiInUserMode,
    thingName,
    desiredSettings,
    reportedSettings,
    desiredConfiguration,
    reportedConfiguration,
    settingsInTransit,
    configurationInTransit,
    additionalInformation,
  } = props;

  const isCfuDevice = !!props.cfu;

  const [formIsInEditMode, setFormInEditMode] = useState(false);

  const [error, setError] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // `loading` covers fetching the device schema and replaces the whole form with a spinner.
  // Saving is tracked separately, because unmounting the form while a save is in flight throws away the user's edits.
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [augmentedDeviceSchema, setAugmentedDeviceSchema] = useState({});
  const [newUiSchema, setNewUiSchema] = useState({});

  const settings = desiredSettings || empty;
  const configuration = desiredConfiguration || empty;
  const settingsDiff = calculateDiffObject(desiredSettings, reportedSettings);
  const configurationDiff = calculateDiffObject(desiredConfiguration, reportedConfiguration);

  const formRef = useRef<any | null>(null);
  const batteryTitleToReferenceMap = useRef<Dictionary<string>>({});
  const formData = generateNewFormConfig(configuration, settings, additionalInformation, isCfuDevice);

  const { deviceSchemaApiClientStatus, setThingName, triggerDeviceSchema } = useDeviceSchemaApiHook();

  useEffect(() => {
    setThingName(thingName);
  }, [thingName]);

  useEffect(() => {
    setLoading(deviceSchemaApiClientStatus.clientStatus === ApiClientStatusEnum.Busy);

    if (deviceSchemaApiClientStatus.clientStatus === ApiClientStatusEnum.Done) {
      batteryTitleToReferenceMap.current = generateBatteryDefinitionSchemaMap(deviceSchemaApiClientStatus.data);

      const _sanitizedDeviceSchema = sanitizeDeviceRawSchema(
        deviceSchemaApiClientStatus.data,
        renderUiInUserMode,
        hasUserEnabledAdvancedUi,
        configuration,
        isCfuDevice,
      );

      if (_sanitizedDeviceSchema.definitions !== undefined) {
        sortBatteryTitles(_sanitizedDeviceSchema.definitions);
      }

      // This is a hack to speed-up the validation of the form. Apparently, AJF caches globally the $refs
      // and then on each validation invocation, it attempts to perform a deep-comparison with the schema.
      // This leads to insane amounts of time wasted on meaningless diffs, which is much slower than
      // recreating the validators every time.
      validator.reset();

      const augmentedSchema = augmentDeviceSchema(_sanitizedDeviceSchema);
      setAugmentedDeviceSchema(augmentedSchema);
    }
    // TODO: Handle failure to get device schema
  }, [deviceSchemaApiClientStatus.clientStatus]);

  useEffect(() => {
    const _newUiSchema: any = generateUiSchemaBasedOnConfiguration(settings, configuration, augmentedDeviceSchema, isCfuDevice);
    setNewUiSchema(_newUiSchema);
  }, [settings, configuration, augmentedDeviceSchema]);

  const { triggerDeviceData } = useDeviceDataApiHook();

  const debouncedValidation = debounce(
    () => {
      if (formRef.current) {
        setError(!formRef.current.validateForm());
      }
    },
    300,
    { maxWait: 2000 },
  );

  const formProps: FormProps<any, RJSFSchema, FormContext> = {
    ref: formRef,
    schema: augmentedDeviceSchema,
    uiSchema: newUiSchema,
    noValidate: true,
    formData,
    validator,
    formContext: {
      diffObj: {
        settings: settingsDiff,
        configuration: configurationDiff,
      },
      batterySchemaMap: batteryTitleToReferenceMap.current,
    },

    className: 'configurationForm confFormCommon' + (formIsInEditMode ? '' : ' configurationFormDisabled'),
    disabled: !formIsInEditMode,
    liveValidate: false,
    omitExtraData: true,
    showErrorList: false,
    transformErrors: (e, s) => transformErrors(e, s, formRef.current),
    templates: {
      FieldTemplate: CustomFieldTemplate,
    },
    experimental_defaultFormStateBehavior: {
      emptyObjectFields: 'populateAllDefaults',
      allOf: 'populateDefaults',
    },
    onBlur: () => {
      debouncedValidation();
    },
    onChange: () => {
      debouncedValidation();
    },
    customValidate,
  };

  const hasSchemaFetchingFailed = deviceSchemaApiClientStatus.clientStatus === ApiClientStatusEnum.Error;

  const toggleFormEditMode = (enableEditMode: boolean) => {
    setSubmitError(null);
    setFormInEditMode(enableEditMode);
  };

  const submitFormData = async () => {
    const formIsValid = formRef.current?.validateForm();
    if (!formIsValid) {
      return;
    }

    const {
      current: {
        state: { formData },
      },
    } = formRef;

    const { settings: formSettings, configuration: { address, addressObj, converter: { brand, model } = {}, reference } = {} } = formData || {};

    setSubmitting(true);
    setSubmitError(null);

    try {
      const configurationRequestDto = generateNewConfigurationDto(
        settings,
        desiredConfiguration,
        desiredSettings,
        formSettings,
        DeviceDynamicDataSubscriber.clientToken,
        addressObj,
        address,
        brand,
        model,
        reference,
        additionalInformation,
        isCfuDevice,
      );

      await API.post('IO_API', `/devices/${thingName}/config`, { body: configurationRequestDto });

      await triggerDeviceData();

      toggleFormEditMode(false);
    } catch (e: any) {
      console.error('Failed to save the device configuration', e);

      // The form stays mounted and in edit mode, so the user's edits are preserved and can be resubmitted.
      setSubmitError(getSubmitErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelFormEdits = () => {
    toggleFormEditMode(false);
  };

  const editableForm = useMemo(() => {
    if (!formIsInEditMode) {
      return null;
    }

    return (
      <Form {...formProps}>
        <div></div>
      </Form>
    );
  }, [formIsInEditMode]);

  if (loading) {
    return <CircularProgressLoader />;
  }

  if (hasSchemaFetchingFailed) {
    return <SchemaLoadingFailureAlertBox onRefresh={triggerDeviceSchema} />;
  }

  const formToRender = formIsInEditMode ? (
    editableForm
  ) : (
    <Form {...formProps}>
      <div></div>
    </Form>
  );

  const disabledFormSubmitButton = submitting || error;

  let alertToRender: JSX.Element | null = null;

  if (submitError) {
    alertToRender = (
      <Alert
        severity="error"
        onClose={() => setSubmitError(null)}
      >
        {submitError}
      </Alert>
    );
  } else if (error) {
    alertToRender = <Alert severity="error">The form contains errors, please fix them before submitting.</Alert>;
  } else if (settingsInTransit || configurationInTransit) {
    alertToRender = <Alert severity="info">Configuration is not applied yet, please wait...</Alert>;
  }

  return (
    <>
      <div className="device-details-form-title">
        {alertToRender}

        <div className="device-details-form-title-buttons">
          {formIsInEditMode ? (
            <>
              <Button
                disabled={disabledFormSubmitButton}
                onClick={submitFormData}
              >
                {submitting ? <CircularProgress size={20} /> : 'Submit'}
              </Button>
              <Button
                disabled={submitting}
                onClick={cancelFormEdits}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => toggleFormEditMode(true)}
              >
                Edit configuration
              </Button>

              {props.cfu?.enphase && <CloudFlexActionPopup thingName={thingName} />}
            </>
          )}
        </div>
      </div>

      <div className="device-details-form-wrapper">{settings ? formToRender : <div>No requested configuration found</div>}</div>
    </>
  );
}

type CloudFlexActionPopupProps = {
  thingName: string;
};

function getOauthUrl(listResponse: IntegrationListResponse, brand: string): string | undefined {
  const integration = listResponse.find((l) => l.brand === brand);
  return integration?.uri;
}

function CloudFlexActionPopup(props: CloudFlexActionPopupProps) {
  const [isDialogVisible, setVisible] = useState(false);
  const [integrationMissing, setIntegrationMissing] = useState(false);

  const { integrationsListApiClientStatus, fetchIntegrationsList, triggerIntegrationsList } = useIntegrationsApiHook();

  const isLoadingIntegrations =
    integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Busy || integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Idle;
  const hasIntegrationsListFailed = integrationsListApiClientStatus.clientStatus === ApiClientStatusEnum.Error || integrationMissing;

  const performOauth = async (forceUpdate: boolean) => {
    setVisible(true);
    setIntegrationMissing(false);

    // We use the value that is returned from the API invocation instead of the `integrationsListApiClientStatus`,
    // because the parent scope will be updated AFTER the completed invocation of performOauth function.
    let status: ApiClientStatus<IntegrationListResponse>;

    if (forceUpdate) {
      status = await triggerIntegrationsList();
    } else {
      status = await fetchIntegrationsList(props.thingName);
    }

    if (status.clientStatus !== ApiClientStatusEnum.Done) {
      return;
    }

    const enphaseOauthUrl = getOauthUrl(status.data!, 'enphase');
    if (!enphaseOauthUrl) {
      console.log('Failed to find integration in list');
      setIntegrationMissing(true);
      return;
    }

    window.location.href = enphaseOauthUrl!;
  };

  let PanelContent: JSX.Element | null = null;

  if (isLoadingIntegrations) {
    PanelContent = (
      <div className="fleet-actions__cloudflex-loading-panel">
        <CircularProgress />
        <span className="cloudflex-loading-label">Loading CloudFlex configuration...</span>
      </div>
    );
  } else if (hasIntegrationsListFailed) {
    PanelContent = (
      <>
        <Alert severity="error">Failed to retrieve CloudFlex Integration options, please try again</Alert>
        <Button
          variant="outlined"
          color="secondary"
          style={{ marginTop: '16px', backgroundColor: '#ffffff' }}
          onClick={async () => {
            await performOauth(true);
          }}
        >
          Retry
        </Button>
      </>
    );
  }

  return (
    <>
      <Button
        color="secondary"
        variant="outlined"
        onClick={() => performOauth(false)}
      >
        CloudFlex Integration
      </Button>

      <Dialog open={isDialogVisible}>
        <DialogTitle>Enable CloudFlex access to Enphase</DialogTitle>

        <DialogContent sx={{ overflow: 'visible', pt: 2 }}>{PanelContent}</DialogContent>

        <DialogActions>
          <Button onClick={() => setVisible(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
